import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    type PayoutAdminTicketV1ResolveTicketRequest,
    type AdminPayoutTicketServiceListTicketsParams,
} from '~/generated/payoutBackend/model';
import {
    adminPayoutTicketServiceListTickets,
    adminPayoutTicketServiceResolveTicket,
    getAdminPayoutTicketServiceListTicketsUrl,
} from '~/generated/payoutBackend/ticket';
import { type TicketStatus } from '../model';

type TicketsParams = Omit<AdminPayoutTicketServiceListTicketsParams, 'filter.statuses'> & {
    'filter.statuses'?: TicketStatus[];
};

export const useTicketsList = (params?: TicketsParams, refetchInterval?: number | false) => {
    return useQuery({
        refetchInterval,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayoutTicketServiceListTickets(params as AdminPayoutTicketServiceListTicketsParams),
        queryKey: [
            queryKeys.PAYOUT_TICKETS,
            getAdminPayoutTicketServiceListTicketsUrl(params as AdminPayoutTicketServiceListTicketsParams),
        ],
    });
};

interface UseTicketMutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

export const useResolveTicket = (options?: UseTicketMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminTicketV1ResolveTicketRequest) => {
            return await adminPayoutTicketServiceResolveTicket(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_TICKETS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Тикет закрыт',
                    });
                });
        },
    });
};
